"use client";
/**
 * User-arrangeable order for a section's sub-tab bar (Market Timing, Settings,
 * …). The top nav has been user-configurable for a while (see
 * {@link useNavConfig}); its sub-tab bars were still shipped-order-only, so a
 * page you open every morning could sit behind six you never touch.
 *
 * Model: per section, an ordered list of tab hrefs. Stored as hrefs rather than
 * indices so the arrangement survives a release that adds, removes or reorders
 * the shipped defaults — {@link orderTabs} merges the saved order against the
 * defaults on every read:
 *   - a saved href that no longer ships is dropped,
 *   - a newly-shipped tab appends at the end (visible, never silently lost),
 *   - anything else keeps the user's position.
 * That's the same merge `loadNav()` does for the top nav, for the same reason:
 * a stale persisted list must never hide a page.
 *
 * Persisted per section to `mpp:nav:subtabs:{key}:v1` through
 * `safeLocalStorageSet`, so the prefs-sync layer mirrors it to the server (a
 * raw localStorage write would be clobbered on the next hydrate) and the
 * arrangement follows the user across browsers.
 */
import { create } from "zustand";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";

const keyFor = (section: string) => `mpp:nav:subtabs:${section}:v1`;
const hiddenKeyFor = (section: string) => `mpp:nav:subtabs-hidden:${section}:v1`;

/**
 * Drag payload type shared by the in-bar tabs and the Settings list.
 *
 * A custom MIME rather than `text/plain` for two reasons: an unrelated drag
 * (a file, a link from another app) is never mistaken for a reorder, and the
 * source href travels IN the payload — readable synchronously on drop, unlike
 * React state, which hasn't re-rendered when the first dragover/drop closure
 * after dragstart runs.
 */
export const SUBTAB_DND_MIME = "application/x-mpp-subtab";

function loadList(key: string): string[] {
  try {
    const raw = safeLocalStorageGet(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((h): h is string => typeof h === "string");
  } catch {
    return [];
  }
}

function saveList(key: string, hrefs: string[]): void {
  try {
    safeLocalStorageSet(key, JSON.stringify(hrefs));
  } catch {
    /* quota / private mode — keep the in-session value anyway */
  }
}

/**
 * Apply a saved order to the shipped tab list. Pure + total: any saved list
 * (empty, stale, partial, duplicated) yields exactly the shipped set, once
 * each, so a corrupt value degrades to the default rather than losing tabs.
 */
export function orderTabs<T extends { href: string }>(tabs: T[], order: string[]): T[] {
  if (order.length === 0) return tabs;
  const byHref = new Map(tabs.map((t) => [t.href, t]));
  const out: T[] = [];
  const taken = new Set<string>();
  for (const href of order) {
    const t = byHref.get(href);
    if (t && !taken.has(href)) {
      out.push(t);
      taken.add(href);
    }
  }
  // Newly-shipped tabs the saved order predates — append, never drop.
  for (const t of tabs) if (!taken.has(t.href)) out.push(t);
  return out;
}

/**
 * Drop hidden tabs. A newly-shipped tab is never in the hidden list, so it
 * always appears — the same "a stale persisted list must not hide a page" rule
 * the order merge follows.
 *
 * Never returns empty while `tabs` is non-empty: if every tab is somehow hidden
 * (a corrupt value, or a release that renamed hrefs), the full list shows
 * through rather than leaving a section with no way to navigate it. The editor
 * separately refuses to hide the last visible tab, so this is the backstop, not
 * the primary guard.
 */
export function visibleTabs<T extends { href: string }>(tabs: T[], hidden: string[]): T[] {
  if (hidden.length === 0) return tabs;
  const hide = new Set(hidden);
  const out = tabs.filter((t) => !hide.has(t.href));
  return out.length > 0 ? out : tabs;
}

type SubTabOrderStore = {
  /** section -> ordered hrefs. Absent/empty means "shipped order". */
  orders: Record<string, string[]>;
  /** section -> hrefs hidden from the bar. Absent/empty means "all visible". */
  hidden: Record<string, string[]>;
  hydrated: Record<string, boolean>;
  /** Read the saved order + hidden set for a section (idempotent, client-only). */
  hydrate: (section: string) => void;
  set: (section: string, hrefs: string[]) => void;
  /** Show/hide one tab. Hiding is reversible from Settings → Pages. */
  toggleHidden: (section: string, href: string) => void;
  /** Back to the shipped order (does NOT unhide — that's its own control). */
  reset: (section: string) => void;
  /** Show every tab again. */
  showAll: (section: string) => void;
};

export const useSubTabOrder = create<SubTabOrderStore>((set, get) => ({
  orders: {},
  hidden: {},
  hydrated: {},
  hydrate: (section) => {
    if (get().hydrated[section]) return;
    set((s) => ({
      orders: { ...s.orders, [section]: loadList(keyFor(section)) },
      hidden: { ...s.hidden, [section]: loadList(hiddenKeyFor(section)) },
      hydrated: { ...s.hydrated, [section]: true },
    }));
  },
  set: (section, hrefs) => {
    saveList(keyFor(section), hrefs);
    set((s) => ({ orders: { ...s.orders, [section]: hrefs } }));
  },
  toggleHidden: (section, href) => {
    const cur = get().hidden[section] ?? [];
    const next = cur.includes(href) ? cur.filter((h) => h !== href) : [...cur, href];
    saveList(hiddenKeyFor(section), next);
    set((s) => ({ hidden: { ...s.hidden, [section]: next } }));
  },
  reset: (section) => {
    saveList(keyFor(section), []);
    set((s) => ({ orders: { ...s.orders, [section]: [] } }));
  },
  showAll: (section) => {
    saveList(hiddenKeyFor(section), []);
    set((s) => ({ hidden: { ...s.hidden, [section]: [] } }));
  },
}));
